// Builds the /treatments/{id}/payments/ payload for a payment collected in the
// call script's AI Booking Wizard, using the SAME shape leadTreatmentPayment()
// expects (see PaymentOption.jsx onSubmit + financialThunks.js) so the backend
// behaves identically to the standard booking flow (paid_at set server-side for
// received modes, Stripe checkout created atomically for 'SEND').
//
// AIBookingWizard's `payment_option` ('No' | 'DEPOSIT' | 'CONSULTATION' | 'TREATMENT')
// already matches PAYMENT_TYPE_OPTIONS in DentallyBookingFormPayment.jsx, so it is
// passed straight through as `payment_type`.
//
// `payment_method` ('PHONE' | 'IN_PRACTICE' | 'PAYMENT_LINK' | 'EMAIL') maps to the
// mode_of_payment values the standard flow uses (PHONE / INPRACTICE / SEND):
//   PHONE        -> received, mode_of_payment 'PHONE'
//   IN_PRACTICE  -> received, mode_of_payment 'INPRACTICE'
//   PAYMENT_LINK -> mode_of_payment 'SEND', delivered by WhatsApp when the lead has a
//                   phone on file (matches the wizard's own "sent via WhatsApp/SMS"
//                   copy), falling back to email
//   EMAIL        -> mode_of_payment 'SEND', delivered by email (matches the wizard's
//                   own "invoice ... sent via email" copy)
//
// Returns:
//   null              — payment_option is 'No'/falsy: nothing to create.
//   { error }         — a payment was requested but required info is missing
//                        (e.g. SEND needs a recipient we don't have) — the caller
//                        should surface `error` and skip the API call.
//   { payload }        — ready to hand to leadTreatmentPayment().
export function buildAIBookingPaymentPayload(appointmentData, leadContact) {
    const { payment_option: paymentOption, payment_amount: paymentAmount, payment_method: paymentMethod } = appointmentData || {};

    if (!paymentOption || paymentOption === 'No') return null;

    const amount = parseFloat(paymentAmount) || 0;
    if (!amount || amount <= 0) {
        return { error: 'Enter a valid payment amount before booking with a deposit or fee.' };
    }

    const email = (leadContact?.email || '').trim();
    const phone = (leadContact?.phone || '').trim();

    if (paymentMethod === 'PHONE' || paymentMethod === 'IN_PRACTICE') {
        return {
            payload: {
                payment_type: paymentOption,
                mode_of_payment: paymentMethod === 'PHONE' ? 'PHONE' : 'INPRACTICE',
                amount,
            },
        };
    }

    if (paymentMethod === 'PAYMENT_LINK' || paymentMethod === 'EMAIL') {
        let channel = 'email';
        if (paymentMethod === 'PAYMENT_LINK' && phone) channel = 'whatsapp';
        const recipientEmail = channel === 'email' || channel === 'both' ? email : '';
        const recipientPhone = channel === 'whatsapp' || channel === 'both' ? phone : '';

        if ((channel === 'email' || channel === 'both') && !recipientEmail) {
            return { error: 'This lead has no email on file to send the payment link to.' };
        }
        if ((channel === 'whatsapp' || channel === 'both') && !recipientPhone) {
            return { error: 'This lead has no phone number on file to send the payment link to.' };
        }

        return {
            payload: {
                payment_type: paymentOption,
                mode_of_payment: 'SEND',
                amount,
                paymentLinkChannel: channel,
                recipientEmail,
                recipientPhone,
            },
        };
    }

    return { error: 'Select how the payment will be taken before booking.' };
}

export default buildAIBookingPaymentPayload;
